import { Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { usePageMeta } from '@/hooks/usePageMeta';
import { allCategories } from '@/utils/news';

const team = [
  { name: '编辑部', role: '内容策划与选题', icon: 'ri-draft-line', desc: '负责每日热点的筛选、整理与专题策划。' },
  { name: '数据组', role: '趋势与热度分析', icon: 'ri-bar-chart-box-line', desc: '跟踪话题热度变化，输出趋势观察。' },
  { name: '产品与技术', role: '平台体验优化', icon: 'ri-code-s-slash-line', desc: '打磨搜索、分类与阅读体验。' },
];

export default function AboutPage() {
  usePageMeta(
    '关于我们 - 热搜榜',
    '了解热搜榜的定位、内容范围与联系方式。热搜榜是聚焦实时热门话题与趋势资讯的平台，覆盖社会、娱乐、科技等领域。',
  );

  return (
    <Layout>
      <div className="border-b border-background-200 bg-background-50">
        <div className="mx-auto max-w-page px-4 py-6 md:px-6">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />
          <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">关于热搜榜</h1>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600">
            一个把“今天大家在关注什么”讲清楚的资讯平台。
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-page px-4 py-8 md:px-6 md:py-10">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="flex flex-col gap-8 lg:col-span-2">
            <section className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-8">
              <h2 className="font-heading text-lg font-bold text-foreground-900 md:text-xl">网站简介</h2>
              <p className="mt-3 text-base leading-8 text-foreground-700">
                热搜榜是聚焦实时热门话题与趋势资讯的资讯平台，覆盖
                <strong className="mx-1 text-primary-700">社会、娱乐、科技</strong>
                三大板块。我们希望用清晰的分类、便捷的搜索与舒适的阅读体验，帮助广大网民、新闻爱好者与信息追踪者，在最短时间内了解当下正在发生、正在被讨论的热点事件。
              </p>
              <p className="mt-4 text-base leading-8 text-foreground-700">
                平台以“热点聚合 + 分类浏览 + 关键词检索”为核心，既能看到实时的热点头条与热搜排行，也能按分类、时间段与热点标签深入筛选，还能在详情页阅读完整的报道脉络与相关内容。
              </p>
              <div className="mt-5 rounded-md border border-accent-200 bg-accent-50 p-4 text-sm leading-relaxed text-accent-900">
                <i className="ri-information-line mr-1"></i>
                特别说明：本站为产品演示站点，站内所有资讯标题、正文、数据、来源与人物均为虚构样例，不包含任何真实用户信息，也不构成任何真实报道或信息发布。
              </div>
            </section>

            <section className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-8">
              <h2 className="font-heading text-lg font-bold text-foreground-900 md:text-xl">内容范围</h2>
              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
                {allCategories.map((cat) => (
                  <div key={cat.slug} className="rounded-lg border border-background-200 bg-background-100 p-4">
                    <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-100 text-xl text-primary-700">
                      <i className={cat.icon}></i>
                    </span>
                    <h3 className="mt-3 font-heading text-base font-bold text-foreground-900">{cat.name}</h3>
                    <p className="mt-1 text-xs leading-relaxed text-foreground-500">{cat.description}</p>
                    <Link
                      to={`/category/${cat.slug}`}
                      className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-primary-600 transition-colors hover:text-primary-700"
                    >
                      查看{cat.name}热点
                      <i className="ri-arrow-right-line"></i>
                    </Link>
                  </div>
                ))}
              </div>
            </section>

            <section className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-8">
              <h2 className="font-heading text-lg font-bold text-foreground-900 md:text-xl">运营团队</h2>
              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
                {team.map((member) => (
                  <div key={member.name} className="rounded-lg border border-background-200 bg-background-100 p-4 text-center">
                    <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-secondary-100 text-2xl text-secondary-700">
                      <i className={member.icon}></i>
                    </span>
                    <h3 className="mt-3 font-heading text-base font-bold text-foreground-900">{member.name}</h3>
                    <p className="text-xs text-accent-700">{member.role}</p>
                    <p className="mt-2 text-xs leading-relaxed text-foreground-500">{member.desc}</p>
                  </div>
                ))}
              </div>
            </section>
          </div>

          <aside className="lg:col-span-1">
            <div className="sticky top-24 flex flex-col gap-6">
              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h2 className="font-heading text-base font-bold text-foreground-900">联系方式</h2>
                <ul className="mt-3 flex flex-col gap-3 text-sm text-foreground-700">
                  <li className="flex items-center gap-2">
                    <i className="ri-mail-line text-base text-primary-600"></i>
                    contact@hotsearch.demo
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-phone-line text-base text-primary-600"></i>
                    010-8888-6666（示例）
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-map-pin-line text-base text-primary-600"></i>
                    北京市朝阳区资讯大厦 8 层
                  </li>
                </ul>
                <div className="mt-4 flex items-center gap-2">
                  {[
                    { icon: 'ri-weibo-fill', label: '微博' },
                    { icon: 'ri-wechat-fill', label: '微信' },
                    { icon: 'ri-tiktok-fill', label: '抖音' },
                    { icon: 'ri-bilibili-fill', label: '哔哩哔哩' },
                  ].map((item) => (
                    <span
                      key={item.label}
                      title={`${item.label}（演示）`}
                      className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full bg-secondary-100 text-base text-secondary-700 transition-colors hover:bg-primary-500 hover:text-background-50"
                    >
                      <i className={item.icon}></i>
                    </span>
                  ))}
                </div>
              </div>

              <div className="rounded-lg border border-background-200 bg-primary-50 p-5">
                <h2 className="font-heading text-base font-bold text-foreground-900">版权与免责声明</h2>
                <p className="mt-2 text-xs leading-relaxed text-foreground-600">
                  本站为产品演示用途，站内所有内容（含标题、正文、图片、数据、来源及人物）均为虚构样例，版权归演示项目所有。
                </p>
                <p className="mt-2 text-xs leading-relaxed text-foreground-600">
                  站内不提供真实数据接口，搜索、筛选、分页与分享等功能均为前端演示效果，不产生任何真实信息发布或数据存储。如内容涉及权利问题，请通过上述邮箱与我们联系。
                </p>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </Layout>
  );
}